/* ============================================================
   Dathent — Welcome-aboard overlay (post-onboarding)
   Port of design/pages/onb-celebrate.jsx to ES modules.

   Three phases:
     0 — brand orb lifts off with ripple rings
     1 — status pills morph in (one per extracted-profile dimension)
     2 — headline + CTAs slide up + confetti rains
   ============================================================ */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons as I } from '../components/pack-icons';
import { platformDisplayName } from '../lib/platforms.js';
import { useFreePreview } from '../contexts/FreePreviewContext.jsx';
import { isFirstPostOnboarding } from '../lib/freeOnboarding.js';

const CONFETTI_PALETTE = ['#4FC3FF', '#7A5BFF', '#E455CF', '#F5B544', '#15803D', '#0A66C2'];

function buildPills(extracted, firstName, t) {
  // Real onboarding fills these from the AI session — fall back to friendly
  // defaults when a field is missing, so the line never collapses to one
  // sad pill on a brand-new profile.
  const ex = extracted || {};
  const tone = Array.isArray(ex.toneOfVoice) && ex.toneOfVoice.length
    ? ex.toneOfVoice.slice(0, 3).join(' · ')
    : (ex.customToneDescription || t('onboardingCelebrate.toneFallback'));
  const audience = ex.targetAudience
    ? truncate(ex.targetAudience, 28)
    : t('onboardingCelebrate.audienceFallback');
  const visual = ex.visualStyle
    ? truncate(ex.visualStyle, 24)
    : (ex.imageMood || t('onboardingCelebrate.visualFallback'));
  const langs = Array.isArray(ex.preferredLanguages) && ex.preferredLanguages.length
    ? ex.preferredLanguages.map((l) => l.toUpperCase()).slice(0, 4).join(' · ')
    : 'EN';
  const platforms = Array.isArray(ex.preferredPlatforms) && ex.preferredPlatforms.length
    ? ex.preferredPlatforms.slice(0, 3).map((p) => platformDisplayName(p)).join(' · ')
    : t('onboardingCelebrate.platformsFallback');

  return [
    { l: t('onboardingCelebrate.brandVoiceLabel'), v: tone },
    { l: t('onboardingCelebrate.audienceLabel'), v: audience },
    { l: t('onboardingCelebrate.visualLabel'), v: visual },
    { l: t('onboardingCelebrate.languagesLabel'), v: langs },
    { l: t('onboardingCelebrate.platformsLabel'), v: platforms },
  ];
}

function truncate(s, n) {
  if (!s) return s;
  return s.length > n ? s.slice(0, n - 1).trim() + '…' : s;
}

function spawnConfetti(containerEl) {
  if (!containerEl) return;
  containerEl.innerHTML = '';
  for (let i = 0; i < 70; i++) {
    const s = document.createElement('span');
    s.style.left = Math.random() * 100 + '%';
    s.style.background = CONFETTI_PALETTE[Math.floor(Math.random() * CONFETTI_PALETTE.length)];
    s.style.setProperty('--tx', Math.random() * 240 - 120 + 'px');
    s.style.setProperty('--rot', Math.random() * 720 + 360 + 'deg');
    s.style.setProperty('--delay', (Math.random() * 1.6).toFixed(2) + 's');
    s.style.setProperty('--d', (2.8 + Math.random() * 2.6).toFixed(2) + 's');
    s.style.width = 5 + Math.random() * 6 + 'px';
    s.style.height = 8 + Math.random() * 8 + 'px';
    s.style.borderRadius = Math.random() > 0.6 ? '999px' : '2px';
    containerEl.appendChild(s);
  }
}

export function OnboardingCelebrate({ firstName, extracted, firstPostId, onDone, preview = false }) {
  const { t } = useTranslation();
  const freePreview = useFreePreview();
  const [phase, setPhase] = useState(0);
  const confettiRef = useRef(null);
  const hasPost = Boolean(firstPostId) && !preview;

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 900);
    const t2 = setTimeout(() => setPhase(2), 1700);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Confetti fires once when we reach phase 2
  useEffect(() => {
    if (phase === 2) spawnConfetti(confettiRef.current);
  }, [phase]);

  const pills = buildPills(extracted, firstName, t);
  const name = firstName?.trim() || t('onboardingCelebrate.nameFallback');

  return (
    <div className="oc-root">
      <div className="oc-bg" />
      <div className="oc-confetti" ref={confettiRef} />

      <div className={`oc-orb-wrap phase-${phase}`}>
        <span className="oc-orb-ring" />
        <span className="oc-orb-ring oc-orb-ring-2" />
        <span className="oc-orb-ring oc-orb-ring-3" />
        <div className="oc-orb">
          <img src="/logo.png" alt="" width="56" height="56"
               style={{ borderRadius: '50%', display: 'block' }} />
        </div>
      </div>

      {phase >= 1 && (
        <div className="oc-status-line">
          {pills.map((p, i) => (
            <div key={i} className="oc-pill" style={{ animationDelay: i * 80 + 'ms' }}>
              <span className="oc-pill-check"><I.check size={10} /></span>
              <span className="oc-pill-l">{p.l}</span>
              <span className="oc-pill-v">{p.v}</span>
            </div>
          ))}
        </div>
      )}

      {phase >= 2 && (
        <>
          <div className="oc-headline">
            <div className="oc-eyebrow">{t(preview ? 'freePreview.celebrateEyebrow' : 'onboardingCelebrate.eyebrow')}</div>
            <h1>{t('onboardingCelebrate.welcomeAboard')}<br /><span className="oc-ac">{t('onboardingCelebrate.namePunctuated', { name })}</span></h1>
            <p>
              {preview
                ? t(isFirstPostOnboarding(freePreview.entitlements) ? 'firstPostOnboarding.celebrateBody' : 'freePreview.celebrateBody')
                : hasPost
                  ? t('onboardingCelebrate.bodyWithPost')
                  : t('onboardingCelebrate.bodyNoPost')}
            </p>
          </div>

          <div className="oc-cta">
            {hasPost ? (
              <>
                <button type="button" className="oc-cta-primary" onClick={() => onDone?.('open_post')}>
                  <I.sparkles size={14} /> {t('onboardingCelebrate.openFirstPost')}
                </button>
                <button type="button" className="oc-cta-ghost" onClick={() => onDone?.('skip')}>
                  {t('onboardingCelebrate.goToDashboard')} <I.arrowRight size={13} />
                </button>
              </>
            ) : (
              // "Show me around" tour is unfinished — disabled for now; just route
              // to the workspace (no ?tour=1).
              <button type="button" className="oc-cta-primary" onClick={() => onDone?.('skip')}>
                {t(preview ? 'freePreview.celebrateCta' : 'onboardingCelebrate.goToDashboard')} <I.arrowRight size={13} />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default OnboardingCelebrate;
